// client/src/components/gemv2/GemV2BidsPage.js

import React, { useEffect, useMemo, useState } from "react";
import { apiGetJson, clamp, fmtDate } from "./gemV2Api";
import Layout from '../Layout';


const COLORS = {
  text: "#1f2937",
  muted: "#6b7280",
  border: "#e5e7eb",
  border2: "#f3f4f6",
  bgHead: "#f9fafb",
  link: "#2563eb",
};

const containerStyle = { maxWidth: 1200, margin: "0 auto", padding: 16, color: COLORS.text };
const cardStyle = {
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  padding: 12,
  background: "#fff",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
};
const rowStyle = { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" };
const inputStyle = { padding: "10px 12px", borderRadius: 10, border: "1px solid #d1d5db", minWidth: 260 };
const selectStyle = { padding: "10px 12px", borderRadius: 10, border: "1px solid #d1d5db" };
const btnStyle = {
  padding: "9px 12px",
  borderRadius: 10,
  border: "1px solid #d1d5db",
  background: "#fff",
  cursor: "pointer",
  color: COLORS.text,
};
const btnActiveStyle = { ...btnStyle, border: "1px solid #111827", fontWeight: 700 };

const smallMuted = { color: COLORS.muted, fontSize: 12 };
const tableWrap = { overflowX: "auto", borderRadius: 12, border: `1px solid ${COLORS.border}` };
const thStyle = {
  textAlign: "left",
  padding: 10,
  background: COLORS.bgHead,
  borderBottom: `1px solid ${COLORS.border}`,
  whiteSpace: "nowrap",
  verticalAlign: "top",
  lineHeight: 1.2,
};
const tdStyle = { padding: 10, borderBottom: `1px solid ${COLORS.border2}`, verticalAlign: "top" };
const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" };
const headerFilterInputStyle = {
  width: "100%",
  minWidth: 0,
  padding: "6px 7px",
  borderRadius: 8,
  border: "1px solid #d1d5db",
};
const headerFilterDateStyle = { ...headerFilterInputStyle, padding: "4px 6px" };

const COLUMNS = [
  { key: "BidNumber", label: "Bid Number", kind: "text", sortField: "BidNumber" },
  { key: "EndDate", label: "End", kind: "date", sortField: "EndDate", dateFromKey: "endDateFrom", dateToKey: "endDateTo" },
  { key: "StartDate", label: "Start", kind: "date", sortField: "StartDate", dateFromKey: "startDateFrom", dateToKey: "startDateTo" },
  { key: "State", label: "State", kind: "text", sortField: "State" },
  { key: "District", label: "District", kind: "text", sortField: "District" },
  { key: "BidType", label: "Type", kind: "text", sortField: "BidType" },
  { key: "Ministry", label: "Ministry", kind: "text", sortField: "Ministry" },
  { key: "Department", label: "Department", kind: "text", sortField: "Department" },
  { key: "Items", label: "Items", kind: "text", sortField: null },
  { key: "Quantity", label: "Qty", kind: "text", sortField: null },
];

const ALLOWED_SORT = [
  "EndDate", "StartDate", "BidNumber", "State", "District", "BidType", "Ministry", "Department", "firstSeenAt",
];

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getSearchTerms(search) {
  return String(search || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function useDebouncedValue(value, delayMs = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function Highlight({ value, terms, filterTerm }) {
  const str = value == null ? "" : String(value);
  if (!str) return null;

  const allTerms = [
    ...(terms || []),
    ...String(filterTerm || "").split(",").map((s) => s.trim()).filter(Boolean),
  ];
  const clean = allTerms.map((t) => t.trim()).filter((t) => t.length >= 2);
  if (!clean.length) return <>{str}</>;

  const pattern = clean.map(escapeRegExp).join("|");
  let re;
  try {
    re = new RegExp(`(${pattern})`, "ig");
  } catch {
    return <>{str}</>;
  }

  const parts = str.split(re);
  return (
    <>
      {parts.map((p, i) => {
        const isHit = clean.some((t) => p.toLowerCase() === t.toLowerCase());
        return isHit ? (
          <mark key={i} style={{ padding: "0 2px", borderRadius: 4 }}>
            {p}
          </mark>
        ) : (
          <span key={i}>{p}</span>
        );
      })}
    </>
  );
}

function SortHeader({ label, field, sortField, sortOrder, onSort, loading }) {
  const sortable = field && ALLOWED_SORT.includes(field);
  const active = sortField === field;
  const arrow = sortable ? (active ? (sortOrder === "ASC" ? " \u25B2" : " \u25BC") : " \u2195") : "";

  return (
    <th
      style={{
        ...thStyle,
        cursor: sortable && !loading ? "pointer" : "default",
        opacity: loading ? 0.7 : 1,
        fontWeight: active ? 800 : 700,
      }}
      onClick={() => sortable && !loading && onSort(field)}
      title={sortable ? "Click to sort" : ""}
    >
      <span>{label}</span>
      {sortable ? <span style={{ color: active ? COLORS.text : "#9ca3af" }}>{arrow}</span> : null}
    </th>
  );
}

function Pagination({ page, totalPages, onPage, loading }) {
  const safeTotal = Math.max(1, totalPages || 1);

  return (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <button style={btnStyle} onClick={() => onPage(clamp(page - 1, 1, safeTotal))} disabled={page <= 1 || loading}>
        Prev
      </button>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={smallMuted}>Page</span>
        <input
          style={{ ...inputStyle, minWidth: 90, width: 90 }}
          value={page}
          onChange={(e) => {
            const v = parseInt(e.target.value || "1", 10);
            onPage(clamp(Number.isNaN(v) ? 1 : v, 1, safeTotal));
          }}
        />
        <span style={smallMuted}>of {safeTotal}</span>
        {loading ? <span className="tm-spinner" title="Loading" /> : null}
      </div>

      <button style={btnStyle} onClick={() => onPage(clamp(page + 1, 1, safeTotal))} disabled={page >= safeTotal || loading}>
        Next
      </button>
    </div>
  );
}

export default function GemV2BidsPage() {
  const [status, setStatus] = useState("live");

  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [sortField, setSortField] = useState("StartDate");
  const [sortOrder, setSortOrder] = useState("DESC");

  const [filters, setFilters] = useState({
    BidNumber: "",
    State: "",
    District: "",
    BidType: "",
    Ministry: "",
    Department: "",
    Items: "",
    Quantity: "",
    endDateFrom: "",
    endDateTo: "",
    startDateFrom: "",
    startDateTo: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resp, setResp] = useState({ total: 0, data: [], totalPages: 1 });

  const search = useDebouncedValue(searchInput.trim(), 350);
  const debouncedFilters = useDebouncedValue(filters, 350);

  const highlightTerms = useMemo(() => getSearchTerms(search), [search]);

  useEffect(() => {
    setPage(1);
  }, [search, debouncedFilters, limit, sortField, sortOrder]);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    params.set("status", status);
    params.set("page", String(page));
    params.set("limit", String(limit));
    params.set("search", search.trim());
    params.set("sortField", sortField);
    params.set("sortOrder", sortOrder === "ASC" ? "asc" : "desc");
    // Text column filters
    const textCols = ["BidNumber", "State", "District", "BidType", "Ministry", "Department", "Items", "Quantity"];
    textCols.forEach((k) => {
      const v = debouncedFilters[k];
      if (v) params.set(`f_${k}`, v);
    });
    // Date filters
    if (debouncedFilters.endDateFrom) params.set("endDateFrom", debouncedFilters.endDateFrom);
    if (debouncedFilters.endDateTo) params.set("endDateTo", debouncedFilters.endDateTo);
    if (debouncedFilters.startDateFrom) params.set("startDateFrom", debouncedFilters.startDateFrom);
    if (debouncedFilters.startDateTo) params.set("startDateTo", debouncedFilters.startDateTo);
    return `?${params.toString()}`;
  }, [status, page, limit, search, sortField, sortOrder, debouncedFilters]);

  useEffect(() => {
    let mounted = true;

    async function run() {
      try {
        setLoading(true);
        setError("");
        const json = await apiGetJson(`/api/gem-v2/bids${query}`);
        if (!mounted) return;
        setResp(json);
      } catch (e) {
        if (!mounted) return;
        setError(e.message || String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    run();
    return () => { mounted = false; };
  }, [query]);

  function onSort(field) {
    if (!ALLOWED_SORT.includes(field)) return;
    if (sortField === field) {
      setSortOrder((prev) => (prev === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortField(field);
      setSortOrder("DESC");
    }
  }

  function setStatusAndReset(s) {
    setStatus(s);
    setPage(1);
  }

  function setFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function clearAllFilters() {
    setSearchInput("");
    setFilters({
      BidNumber: "", State: "", District: "", BidType: "", Ministry: "", Department: "", Items: "", Quantity: "",
      endDateFrom: "", endDateTo: "", startDateFrom: "", startDateTo: "",
    });
    setPage(1);
  }

  return (
    <Layout>
    <div style={containerStyle}>
      <style>{`
        .tm-loadingbar-wrap{
          height: 3px; border-radius: 999px; background: #eef2ff;
          overflow: hidden; margin-top: 10px;
        }
        .tm-loadingbar{
          height: 100%;
          width: 40%;
          background: #111827;
          transform: translateX(-120%);
          animation: tmbar 1.1s infinite;
        }
        @keyframes tmbar{
          0%{ transform: translateX(-120%); opacity: .35; }
          50%{ opacity: .9; }
          100%{ transform: translateX(260%); opacity: .35; }
        }
        .tm-spinner{
          width: 14px; height: 14px; border-radius: 999px;
          border: 2px solid #e5e7eb; border-top-color: #111827;
          animation: tmspin .8s linear infinite;
          display: inline-block;
        }
        @keyframes tmspin{
          from{ transform: rotate(0deg); }
          to{ transform: rotate(360deg); }
        }
      `}</style>

      <div style={{ ...cardStyle, marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, display: "flex", gap: 10, alignItems: "center" }}>
              <span>GeM-Bids</span>
              {loading ? <span className="tm-spinner" title="Loading" /> : null}
            </div>
          </div>

          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: 800 }}>{(resp.total || 0).toLocaleString()} bids</div>
            <div style={smallMuted}>status = {status}</div>
          </div>
        </div>

        {loading ? (
          <div className="tm-loadingbar-wrap" title="Loading">
            <div className="tm-loadingbar" />
          </div>
        ) : null}

        <div style={{ marginTop: 12, ...rowStyle }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={status === "all" ? btnActiveStyle : btnStyle} onClick={() => setStatusAndReset("all")}>
              All
            </button>
            <button style={status === "live" ? btnActiveStyle : btnStyle} onClick={() => setStatusAndReset("live")}>
              Live
            </button>
            <button style={status === "archive" ? btnActiveStyle : btnStyle} onClick={() => setStatusAndReset("archive")}>
              Archive
            </button>
          </div>

          <input
            style={inputStyle}
            placeholder="Search (comma-separated): e.g. cctv,delhi"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(1);
            }}
          />

          <select
            style={selectStyle}
            value={limit}
            onChange={(e) => {
              setLimit(parseInt(e.target.value, 10) || 20);
              setPage(1);
            }}
          >
            <option value={20}>20 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
            <option value={200}>200 / page (max)</option>
          </select>

          <button style={btnStyle} onClick={clearAllFilters}>
            Clear Filters
          </button>

          <div style={{ marginLeft: "auto" }}>
            <Pagination page={page} totalPages={resp.totalPages} onPage={setPage} loading={loading} />
          </div>
        </div>

        {error ? (
          <div style={{ marginTop: 10, color: "#b91c1c", fontWeight: 700 }}>
            Error: {error}
          </div>
        ) : null}
      </div>

      <div style={tableWrap}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <SortHeader
                  key={col.key}
                  label={col.label}
                  field={col.sortField}
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onSort={onSort}
                  loading={loading}
                />
              ))}
            </tr>
            <tr>
              {COLUMNS.map((col) => {
                const baseTh = {
                  ...thStyle,
                  background: "#fff",
                  padding: 6,
                  whiteSpace: "normal",
                };

                if (col.kind === "date") {
                  return (
                    <th key={`${col.key}_filter`} style={baseTh}>
                      <div style={{ display: "grid", gap: 4 }}>
                        <input
                          type="date"
                          style={headerFilterDateStyle}
                          value={filters[col.dateFromKey]}
                          onChange={(e) => setFilter(col.dateFromKey, e.target.value)}
                          title="From date"
                        />
                        <input
                          type="date"
                          style={headerFilterDateStyle}
                          value={filters[col.dateToKey]}
                          onChange={(e) => setFilter(col.dateToKey, e.target.value)}
                          title="To date"
                        />
                      </div>
                    </th>
                  );
                }

                return (
                  <th key={`${col.key}_filter`} style={baseTh}>
                    <input
                      style={headerFilterInputStyle}
                      placeholder="Filter"
                      value={filters[col.key] || ""}
                      onChange={(e) => setFilter(col.key, e.target.value)}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {(resp.data || []).map((r, idx) => (
              <tr key={`${r.BidNumber || "x"}_${idx}`}>
                <td style={{ ...tdStyle, ...mono }}>
                  {r.BidURL ? (
                    <a href={r.BidURL} target="_blank" rel="noreferrer" style={{ color: COLORS.link }}>
                      <Highlight value={r.BidNumber} terms={highlightTerms} filterTerm={debouncedFilters.BidNumber} />
                    </a>
                  ) : (
                    <Highlight value={r.BidNumber} terms={highlightTerms} filterTerm={debouncedFilters.BidNumber} />
                  )}
                </td>

                <td style={tdStyle}>{fmtDate(r.EndDate)}</td>
                <td style={tdStyle}>{fmtDate(r.StartDate)}</td>

                <td style={tdStyle}><Highlight value={r.State || ""} terms={highlightTerms} filterTerm={debouncedFilters.State} /></td>
                <td style={tdStyle}><Highlight value={r.District || ""} terms={highlightTerms} filterTerm={debouncedFilters.District} /></td>
                <td style={tdStyle}><Highlight value={r.BidType || ""} terms={highlightTerms} filterTerm={debouncedFilters.BidType} /></td>
                <td style={tdStyle}><Highlight value={r.Ministry || ""} terms={highlightTerms} filterTerm={debouncedFilters.Ministry} /></td>
                <td style={tdStyle}><Highlight value={r.Department || ""} terms={highlightTerms} filterTerm={debouncedFilters.Department} /></td>

                <td style={{ ...tdStyle, whiteSpace: "pre-wrap" }}>
                  <Highlight value={r.Items || ""} terms={highlightTerms} filterTerm={debouncedFilters.Items} />
                </td>

                <td style={tdStyle}>
                  <Highlight value={r.Quantity || ""} terms={highlightTerms} filterTerm={debouncedFilters.Quantity} />
                </td>
              </tr>
            ))}

            {!loading && (!resp.data || resp.data.length === 0) ? (
              <tr>
                <td style={{ ...tdStyle, color: COLORS.muted }} colSpan={10}>
                  No data found for current filters.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div style={{ ...cardStyle, marginTop: 12 }}>
        <Pagination page={page} totalPages={resp.totalPages} onPage={setPage} loading={loading} />
      </div>
    </div>
  </Layout>
  );
}
